import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEqual } from "lodash";
import { useState } from "react";

import { VisitTypeScheduleConfig } from "@caredna-marlin/utils/configurationTypes";

import BlockSchedule from "./BlockSchedule";
import Help from "~/components/common/Help";
import { VisitType } from "~/providers/OrgConfigProvider";

type VisitTypeProps = {
  visitType: VisitType,
};

// The label is twice the value value, because internally we use the offset both before and after the visit time.
const arrivalWindowOptions: [number, string][] = [
  [0, "None"],
  [5, "10 minutes"],
  [15, "30 minutes"],
  [30, "1 hour"],
  [60, "2 hours"],
  [120, "4 hours"],
];

const daysBeforeLastOptionOptions: [number, string][] = [
  [1, "1 day"],
  [2, "2 days"],
  [3, "3 days"],
  [7, "7 days"],
  [15, "15 days"],
  [30, "30 days"],
  [60, "2 months"],
  [90, "3 months"],
  [180, "6 months"],
  [365, "1 year"],
];

const ConfigureVisitType = ({ visitType }: VisitTypeProps) => {
  const [local, setLocal] = useState<VisitTypeScheduleConfig>(visitType.schedulingConfig);
  const [updateVisitType, { loading }] = useMutation(UPDATE_VISIT_TYPE);

  const noLocalChanges = isEqual(local, visitType.schedulingConfig);

  const [validationErrors, setValidationErrors] = useState(false);

  const onSave = async () => {
    await updateVisitType({
      variables: {
        visitTypeId: visitType.id,
        schedulingConfig: local,
      },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  };

  return <Card sx={{ height: "100%", pl: 1, pt: 1 }}>
    <CardContent>
      <Grid container sx={{ alignItems: "center" }} rowSpacing={2}>
        <Grid size={{ xs: 12, md: 5, lg: 4 }}>
          Scheduling
          <Help text="Visits around the Lead Time from time of scheduling will have higher 'Proximity' scores." />
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 8 }}>
          <TextField
            label="Lead Time (days)"
            type="number"
            size="small"
            sx={{ width: "8em", mr: 2 }}
            value={local.leadTimeDays}
            onChange={e => Number(e.target.value) >= 0 && setLocal({ ...local, leadTimeDays: Number(e.target.value) })}
          />
          <TextField
            label="Max"
            select
            size="small"
            sx={{ width: "8em" }}
            value={local.daysBeforeLastOption}
            onChange={e => setLocal({ ...local, daysBeforeLastOption: Number(e.target.value) })}
          >
            {daysBeforeLastOptionOptions.map((option) => <MenuItem
              key={option[0]}
              value={option[0]}
              disabled={option[0] <= local.leadTimeDays}
            >{option[1]}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 5, lg: 4 }}>
          Arrival Window
          <Help text="The provider arrival window for visits. To help optmize provider routes, we may slide the visit around within this window if necessary." />
        </Grid>

        <Grid size={{ xs: 12, md: 7, lg: 8 }}>
          <TextField
            label="In-Home"
            select
            size="small"
            sx={{ width: "8em", mr: 2 }}
            value={local.arrivalWindowOffsetMinutes}
            onChange={e => setLocal({ ...local, arrivalWindowOffsetMinutes: Number(e.target.value) })}
          >
            {arrivalWindowOptions.map(option => (
              <MenuItem key={option[0]} value={option[0]}>
                {option[1]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Virtual"
            select
            size="small"
            sx={{ width: "8em" }}
            value={local.virtualArrivalWindowOffset}
            onChange={e => setLocal({ ...local, virtualArrivalWindowOffset: Number(e.target.value) })}
          >
            {arrivalWindowOptions.map(option => (
              <MenuItem key={option[0]} value={option[0]}>
                {option[1]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 5, lg: 4 }}>
          Allow Overflow Shifts
          <Help text="Whether or not overflow shifts should be used for this visit type." />
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 8 }}>
          <Switch
            checked={local.allowOverflowShifts}
            disabled={loading}
            onChange={(event) => { setLocal({...local, allowOverflowShifts: event.target.checked })}}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 5, lg: 4 }}>
          Block Schedule
          <Help text="Use set hours (in patient's timezone) for arrival windows. Visits starting outside of these times use offsets." />
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 8 }}>
          <BlockSchedule
            arrivalWindowBlockSchedule={local.arrivalWindowBlockSchedule}
            onChange={arrivalWindowBlockSchedule => setLocal({...local, arrivalWindowBlockSchedule})}
            setValidationErrors={setValidationErrors}
          />
        </Grid>
      </Grid>
    </CardContent>
    <CardActions>
      <Button variant="contained" size="small" color="primary" disabled={noLocalChanges || validationErrors} onClick={onSave} loading={loading}>
        Save
      </Button>
      <Button
        variant="outlined"
        size="small"
        color="secondary"
        onClick={() => setLocal(visitType.schedulingConfig)}
        sx={{ ml: 1 }}
      >
        Reset
      </Button>
    </CardActions>
  </Card>
};

export default ConfigureVisitType;

const UPDATE_VISIT_TYPE = gql(`
  mutation UpdateVisitType(
    $visitTypeId: Int!
    $schedulingConfig: VisitTypeSchedulingConfig!
  )
  @permission(requires: manageOrgConfig)
  {
    updateVisitType(
      id: $visitTypeId
      schedulingConfig: $schedulingConfig
    ) {
      id
      name
      schedulingConfig
    }
  }
`);
